import type { WatchPeriodStatus } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { parse, toSeconds } from 'iso8601-duration';

import { I18n, useI18n } from './useI18n';

const HOURS_IN_DAY = 24;
const MILLISECONDS_IN_HOUR = 1000 * 60 * 60;
const MILLISECONDS_IN_DAY = MILLISECONDS_IN_HOUR * HOURS_IN_DAY;
const DAYS_FOR_START_WATCHING = 30;
const MILLISECONDS_FOR_START_WATCHING = DAYS_FOR_START_WATCHING * MILLISECONDS_IN_DAY;
const MIN_DAYS_FOR_OUTPUT_AS_DAYS = 2;

interface ExpirationText {
    text: string;
    isHighlighted: boolean;
}

const transformDurationToHours = (duration: string | null | undefined) =>
    duration ? Math.floor(toSeconds(parse(duration)) / 3600) : undefined;

function getEndWatchingText(hoursToEndWatching: number, i18n: I18n): string {
    const hours = hoursToEndWatching || 1;
    const days = Math.floor(hours / HOURS_IN_DAY);

    const periodText =
        days >= MIN_DAYS_FOR_OUTPUT_AS_DAYS
            ? `${days} ${i18n('plural_forms__day', { day: days })}`
            : `${hours} ${i18n('plural_forms__hour', { hour: hours })}`;

    return i18n('expiration_end_watch', { periodText });
}

interface StartWatchingTimeInterface {
    createdDate?: string | undefined;
    hoursToStartWatching?: number;
    i18n: I18n;
}

function getStartWatchingText({
    createdDate,
    hoursToStartWatching,
    i18n,
}: StartWatchingTimeInterface): string {
    let time;

    // TODO use lib for dates after https://st.yandex-team.ru/OTT-5594
    const timeDifference =
        typeof hoursToStartWatching === 'number'
            ? MILLISECONDS_FOR_START_WATCHING - hoursToStartWatching * MILLISECONDS_IN_HOUR
            : new Date().getTime() - new Date(createdDate as string | number).getTime();
    const daysDifference = DAYS_FOR_START_WATCHING - timeDifference / MILLISECONDS_IN_DAY;
    const roundedDaysDifference = Math.round(daysDifference);

    if (roundedDaysDifference >= 1) {
        const dayPlural = i18n('plural_forms__day', { day: roundedDaysDifference });

        time = `${roundedDaysDifference} ${dayPlural}`;
    } else {
        const roundedHoursDifference = Math.round(daysDifference * HOURS_IN_DAY);

        if (roundedHoursDifference >= 1) {
            const hourPlural = i18n('plural_forms__hour', { hour: roundedHoursDifference });

            time = `${roundedHoursDifference} ${hourPlural}`;
        } else {
            time = i18n('expiration_less_left_time');
        }
    }

    return i18n('expiration_start_watch', { time });
}

export function useMovieExpirationText(
    watchPeriodStatus: WatchPeriodStatus | null | undefined,
    timeToExpire: string | null | undefined
): ExpirationText | undefined {
    const i18n = useI18n();

    const hoursToExpire = transformDurationToHours(timeToExpire);

    if (watchPeriodStatus === 'WAITING_END_WATCHING') {
        const text =
            typeof hoursToExpire === 'number' ? getEndWatchingText(hoursToExpire, i18n) : '';

        return {
            text,
            isHighlighted: true,
        };
    }

    if (watchPeriodStatus === 'WAITING_START_WATCHING') {
        const text = getStartWatchingText({
            hoursToStartWatching: hoursToExpire,
            i18n,
        });

        return {
            text,
            isHighlighted: typeof hoursToExpire === 'number' ? hoursToExpire <= 2 : false,
        };
    }
}
